import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { reportError } from './sentry';

/**
 * OTA live-update wiring.
 *
 * Both shells (QA `racore.app.qa` and PROD `racore.app`) are self-contained —
 * neither uses `server.url`. They run a locally stored bundle and swap it for a
 * newer one published by the web deploy. QA and PROD therefore exercise the
 * IDENTICAL loading path, which is what makes a QA pass meaningful for PROD.
 *
 * ── Update timing ───────────────────────────────────────────────────────────
 * The plugin runs in `onlyDownload` mode: it fetches new bundles on its own but
 * NEVER applies one by itself. Applying is our decision, taken here, because
 * applying a bundle destroys the JavaScript context — an unsaved form, a
 * half-finished upload or an in-flight mutation dies with it.
 *
 *   • no holds        → apply immediately (this is the "instant" path)
 *   • holds present   → queue via next() so the update lands on the next
 *                       backgrounding regardless, and apply the moment the
 *                       last hold is released, whichever comes first
 *
 * Screens with unsaved state take a hold — see `useDeferOtaUpdate`.
 *
 * ⚠️  notifyAppReady() is not optional. The plugin arms a rollback timer
 * (`appReadyTimeout`, 10s) on every bundle it applies. If the bundle does not
 * call notifyAppReady() before the timer fires, the plugin assumes the bundle
 * is broken and reverts to the previous one — forever, silently. Call it as
 * early as possible and never behind an await.
 */

/** Bundle the plugin has downloaded and we have not applied yet. */
let pendingBundleId: string | null = null;

/** Bundle currently staged with next(), so we never re-stage the same one. */
let stagedBundleId: string | null = null;

/** Listeners are registered exactly once per JS context. */
let initialised = false;

/**
 * A bounded retry budget: N attempts, fixed delay between them.
 *
 * Two independent instances below. They are deliberately NOT one shared budget
 * and NOT shared constants — registering the plugin listeners and applying a
 * downloaded bundle fail for unrelated reasons, and one exhausting must not
 * consume the other's attempts. That the two currently use the same numbers is
 * coincidence; either can be tuned without touching the other.
 */
function createRetryBudget(maxAttempts: number, delayMs: number) {
  let attempts = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cancel = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return {
    /** Count this attempt and return the new attempt number. */
    consume: (): number => (attempts += 1),
    exhausted: (): boolean => attempts >= maxAttempts,

    /**
     * Clear the counter AND drop any retry still in flight.
     *
     * Cancelling is the whole point: a retry scheduled for the PREVIOUS subject
     * carries no reference to it, so if it fires after a reset it silently
     * spends the new subject's budget — and can fire a second, concurrent
     * attempt at something already being applied. Reset without cancel is a
     * half-reset.
     */
    reset: (): void => {
      attempts = 0;
      cancel();
    },

    /** At most one retry outstanding; a new one supersedes any pending one. */
    scheduleRetry: (fn: () => void): void => {
      cancel();
      timer = setTimeout(() => {
        timer = null;
        fn();
      }, delayMs);
    },
  };
}

/** Listener registration. Reset only when the module is re-armed. */
const listenerRetries = createRetryBudget(3, 5000);

/** Applying the CURRENT pending bundle. Reset when a new bundle lands. */
const applyRetries = createRetryBudget(3, 5000);

/** Reasons the app is currently unsafe to reload. Non-empty = defer. */
const holds = new Set<symbol>();

/** Narrow shape we rely on; the plugin's payloads carry more than this. */
type BundleEvent = { bundle?: { id?: string } };

function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

function ota(error: unknown, hook: string, extra: Record<string, unknown> = {}) {
  reportError(error, { scope: 'ota', hook, platform: Capacitor.getPlatform(), ...extra });
}

/**
 * Apply the pending bundle now. Destroys the JS context — nothing after the
 * set() call runs, so this must only ever be reached with zero holds.
 */
function applyPending(): void {
  const id = pendingBundleId;
  if (!id || holds.size > 0) return;

  // NOTE: pendingBundleId is deliberately NOT cleared here. set() destroys the
  // JS context on success, so nothing survives to read it — but if set()
  // *fails*, clearing first would drop the bundle on the floor and no later
  // hold-release would ever retry it.
  const attempt = applyRetries.consume();

  void CapacitorUpdater.set({ id }).catch((error: unknown) => {
    const exhausted = applyRetries.exhausted();

    ota(error, 'set', {
      bundleId: id,
      attempt,
      impact: exhausted
        ? 'bundle not applied this session; will be retried on next launch'
        : 'apply failed; retrying',
    });

    // Retry on a timer rather than waiting for the next hold release. Keeping
    // pendingBundleId set is only half a recovery: applyPending is otherwise
    // reachable ONLY from a hold release, so a user who never touches a form
    // again after the failure would sit on the old bundle for the whole session
    // with the new one downloaded and idle on disk.
    if (!exhausted) applyRetries.scheduleRetry(applyPending);
  });
}

/**
 * Mark the app unsafe to reload (dirty form, upload in flight, payment step…).
 * Returns the release function. ALWAYS call it — a leaked hold silently pins
 * the app to its current bundle for the rest of the session.
 *
 * Prefer the `useDeferOtaUpdate` hook in React code; this is the imperative
 * escape hatch for non-component code (interceptors, stores, sagas).
 */
export function acquireUpdateHold(): () => void {
  const token = Symbol('ota-hold');
  holds.add(token);

  let released = false;
  return () => {
    if (released) return; // idempotent: double-release must not free someone else's hold
    released = true;
    holds.delete(token);

    // Last one out applies whatever is waiting.
    if (holds.size === 0) applyPending();
  };
}

/** True when a downloaded bundle is waiting for a safe moment. */
export function hasPendingUpdate(): boolean {
  return pendingBundleId !== null;
}

export function initLiveUpdates(): void {
  // No-op on the web build — the browser IS the source of truth there.
  if (!isNative()) return;

  // Registering the listeners twice makes onReady fire twice per event, which
  // means a duplicate set()/next() on a bundle already being applied. Cheap to
  // prevent, genuinely nasty to debug. (Vitest calls this per-test, but
  // resetModules() gives each test a fresh module, so the flag resets with it.)
  if (initialised) return;
  initialised = true;

  // Fire-and-forget: must not block first paint, must not be awaited.
  void CapacitorUpdater.notifyAppReady().catch((error: unknown) => {
    // Do NOT rethrow — a failure here must never prevent the app rendering.
    //
    // But it must not be silent either. If this call keeps failing, the plugin
    // rolls the bundle back on EVERY launch: users see the app perpetually
    // revert to an older version and nothing surfaces in logs.
    ota(error, 'notifyAppReady', {
      impact: 'bundle will be rolled back by the plugin after appReadyTimeout',
    });
  });

  const onReady = (event: BundleEvent) => {
    const id = event?.bundle?.id;
    if (!id) return;

    if (id !== pendingBundleId) applyRetries.reset(); // new bundle, fresh budget
    pendingBundleId = id;

    if (holds.size === 0) {
      applyPending();
      return;
    }

    // Something is unsafe to interrupt. Stage the bundle so it also lands on the
    // next backgrounding even if the hold is never cleanly released (crash,
    // forced quit, a screen that forgets to release). Belt and braces: whichever
    // happens first — hold released or app backgrounded — the user gets it.
    //
    // Re-stage when a NEWER bundle supersedes one already staged, otherwise the
    // background path would apply the older bundle while the foreground path
    // applies the newer one — the two would disagree about what "latest" means.
    // Skip when it is the same id: both downloadComplete and updateAvailable
    // fire for one bundle, and next() twice for the same id is undefined
    // behaviour in the plugin.
    if (stagedBundleId === id) return;
    stagedBundleId = id;

    void CapacitorUpdater.next({ id }).catch((error: unknown) => {
      stagedBundleId = null; // staging failed — let a later event retry it
      ota(error, 'next', { bundleId: id });
    });
  };

  void registerListeners(onReady);
}

/**
 * Register the bundle listeners, with retries.
 *
 * If registration fails there is no OTA at all for the rest of the session —
 * nothing downloads, nothing applies, and the only trace is a Sentry event that
 * is easy to miss. The `initialised` guard that stops double-registration would
 * also stop any recovery, so failure has to explicitly re-arm the module rather
 * than leaving it in a permanently dead state.
 *
 * `downloadComplete` is the authoritative "it is on disk" signal. In some plugin
 * versions `updateAvailable` carries the same payload, so we listen to both and
 * let the id-guard in onReady make it idempotent.
 */
function registerListeners(onReady: (event: BundleEvent) => void): Promise<void> {
  const attempt = listenerRetries.consume();

  return Promise.all([
    CapacitorUpdater.addListener('downloadComplete', onReady),
    CapacitorUpdater.addListener('updateAvailable', onReady),
  ])
    .then(() => undefined)
    .catch((error: unknown) => {
      const exhausted = listenerRetries.exhausted();

      ota(error, 'addListener', {
        attempt,
        impact: exhausted
          ? 'OTA is disabled for this session — no bundle can be received'
          : 'listener registration failed; retrying',
      });

      if (exhausted) {
        // Re-arm so a later initLiveUpdates() (app resume, a future caller) can
        // try again instead of the app being stuck without OTA forever.
        initialised = false;
        listenerRetries.reset();
        return;
      }

      listenerRetries.scheduleRetry(() => void registerListeners(onReady));
    });
}
